import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { CampaignType, DspSupplyType, MediaType, StrategyDspCreativeType } from "@front/m19-api-client";
import { dealsDurationRanges, DspCreativeAssetsService, DspService, DspStrategyService } from "@front/m19-services";
import { ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DspCreativeComponent } from "@m19-board/dsp/dsp-creatives/dsp-creative/dsp-creative.component";
import { UploadAmazonLoadingModalComponent } from "@m19-board/shared/media/upload-amazon-loading-modal/upload-amazon-loading-modal.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { of, switchMap } from "rxjs";

@Component({
  selector: "app-strategy-page-dsp-video",
  imports: [DspCreativeComponent, TranslocoDirective],
  template: `@if (strategy(); as strategy) {
    <app-dsp-creative
      *transloco="let t"
      [assetId]="strategy.videoAssetId ?? ''"
      [addAssetLabel]="t('dsp-creatives.add_a_video')"
      [mediaType]="MediaType.videoAsset"
      [funnel]="strategy.dspFunnel!"
      [allowedVideoDurations]="allowedVideoDurations()"
      [allowDelete]="false"
      [isLoading]="isUploadingVideo()"
      (selectedAssetId)="changeVideo($event)"
      (assetToUpload)="uploadVideo($event)"
    />
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageDspVideoComponent {
  private readonly strategyPageService: StrategyPageService<CampaignType.DSP> = inject(StrategyPageService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly dspCreativeAssetsService = inject(DspCreativeAssetsService);
  private readonly dspService = inject(DspService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);

  protected readonly MediaType = MediaType;

  readonly strategy = computed(() => this.strategyPageService.strategy());
  readonly accountMarketplace = this.strategyPageService.accountMarketplace;
  readonly isUploadingVideo = signal(false);

  private readonly videoDeals = toSignal(
    toObservable(this.accountMarketplace).pipe(
      switchMap((am) => (am.dspAdvertiserId ? this.dspService.getDspAdvertiserDeals(am.dspAdvertiserId) : of([]))),
    ),
    { initialValue: [] },
  );

  readonly allowedVideoDurations = computed(() => {
    const strategy = this.strategy();
    if (strategy?.creativeType !== StrategyDspCreativeType.VIDEO_DEAL) {
      return [];
    }
    const dealIds = new Set(
      strategy.dspSupply?.filter((s) => s.supplyType === DspSupplyType.VIDEO_DEAL && s.dealId).map((s) => s.dealId!) ??
        [],
    );
    if (dealIds.size === 0) {
      return [];
    }
    return dealsDurationRanges(this.videoDeals().filter((deal) => dealIds.has(deal.dealId)));
  });

  protected changeVideo(videoAssetId: string) {
    this.isUploadingVideo.set(true);
    const strategyId = this.strategy()!.strategyId;

    this.dspStrategyService.updateDspStrategyVideo(strategyId, videoAssetId).subscribe({
      next: () => this.isUploadingVideo.set(false),
      error: () => this.isUploadingVideo.set(false),
    });
  }

  uploadVideo(file: File) {
    const ref = this.modalService.openModal(UploadAmazonLoadingModalComponent, {
      closeMode: "none",
    });
    this.dspCreativeAssetsService
      .uploadCreativeAsset(
        this.accountMarketplace().accountId,
        this.accountMarketplace().marketplace,
        this.accountMarketplace().dspAdvertiserId!,
        file,
        MediaType.videoAsset,
        this.accountMarketplace().dspAdvertiser!.entityId,
      )
      .subscribe({
        next: (creativeAsset) => {
          this.changeVideo(creativeAsset.assetId);
          this.toasterService.success("Video uploaded successfully");
          ref.close();
        },
        error: (err) => {
          ref.close();
          this.toasterService.error(err, "Video upload failed");
        },
      });
  }
}
